import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Save, Trash2, X as XIcon } from "lucide-react";
import MessageColorPicker from "./MessageColorPicker";

const MAX = 100; // 100 chars, spaces excluded — aligns with the in-page panel.

function countNoSpace(s) {
  return [...(s || "")].filter((c) => !/\s/.test(c)).length;
}

/**
 * Lightweight modal to edit the establishment-wide welcome message — opens
 * from the header shortcut on the merchant dashboard. Uses the same
 * `/merchant/me/global-message` API as the in-page GlobalMessagePanel.
 */
export default function GlobalMessageDialog({ open, commerce, onClose, onSaved }) {
  const [msg, setMsg] = useState(commerce?.globalMessage || "");
  const [color, setColor] = useState(commerce?.globalMessageColor || "blue");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMsg(commerce?.globalMessage || "");
      setColor(commerce?.globalMessageColor || "blue");
    }
  }, [open, commerce?.globalMessage, commerce?.globalMessageColor]);

  if (!open) return null;

  const noSpace = countNoSpace(msg);
  const overLimit = noSpace > MAX;
  const dirty = msg !== (commerce?.globalMessage || "") || color !== (commerce?.globalMessageColor || "blue");

  const handleChange = (e) => {
    const next = e.target.value.toUpperCase();
    if (countNoSpace(next) > MAX + 50) return;
    setMsg(next);
  };

  const save = async () => {
    if (overLimit) {
      toast.error("Message trop long (100 caractères hors espaces)");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/merchant/me/global-message", {
        globalMessage: msg,
        globalMessageColor: color,
      });
      toast.success("Message mis à jour");
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.patch("/merchant/me/global-message", { globalMessage: "", globalMessageColor: color });
      setMsg("");
      toast.success("Message effacé");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="merchant-message-dialog"
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl border border-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-black uppercase tracking-[0.06em] text-base text-ink inline-flex items-center gap-2">
            <Megaphone size={16} className="text-brand" /> Message Établissement
          </h2>
          <button
            onClick={onClose}
            className="text-subtle hover:text-ink"
            aria-label="Fermer"
            data-testid="merchant-message-dialog-close"
          >
            <XIcon size={18} />
          </button>
        </header>
        <div className="p-5">
          <p className="text-xs text-subtle leading-relaxed">
            Message diffusé en bandeau sur l'écran public, indépendamment des files (bienvenue, horaires, info utile…). Limité à <span className="font-bold text-ink">{MAX} caractères</span> (espaces non comptés).
          </p>
          <textarea
            value={msg}
            onChange={handleChange}
            rows={4}
            placeholder='Ex : "Bienvenue dans notre établissement" · "Merci de préparer vos documents"'
            className={`mt-3 w-full border ${overLimit ? "border-destructive" : "border-border"} p-3 text-sm font-mono rounded-md focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand`}
            data-testid="merchant-message-dialog-input"
            autoFocus
          />
          <MessageColorPicker
            value={color}
            onChange={setColor}
            testId="merchant-message-dialog-color"
          />
          <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
            <span
              className={`text-xs font-mono ${overLimit ? "text-destructive font-bold" : "text-subtle"}`}
              data-testid="merchant-message-dialog-counter"
            >
              {noSpace} / {MAX}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={clear}
                disabled={saving || (!msg && !commerce?.globalMessage)}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-destructive/40 text-destructive hover:bg-destructive hover:text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                data-testid="merchant-message-dialog-clear-btn"
              >
                <Trash2 size={12} /> Effacer
              </button>
              <button
                onClick={save}
                disabled={saving || overLimit || !dirty}
                className="inline-flex items-center gap-1.5 h-9 px-4 bg-brand text-white text-xs font-bold uppercase tracking-wider rounded-md disabled:opacity-50 hover:bg-brand-hover"
                data-testid="merchant-message-dialog-save-btn"
              >
                <Save size={12} /> {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
